/**
 * PublisherApiTogglePage — admin API access console (FS-154 / DASH).
 *
 * Single-page surface for provisioning granular API access per affiliate
 * (publisher). Admins can:
 *
 *   - Browse all provisioned affiliates (current enabled state + scopes).
 *   - Select one affiliate to see current toggle + audit history.
 *   - Upsert the full state (enabled + scopes + rate limit + note).
 *   - Flip enabled on / off with a single click (idempotent).
 *
 * No @tanstack/react-query — widgets hydrate in parallel via promises,
 * mirroring PublisherApiPage. Brand rules honoured: emerald->sky gradient
 * reserved for the headline, zinc borders elsewhere, 6-8px rounded,
 * light-theme only, no emojis.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
	AVAILABLE_SCOPES,
	type ApiScope,
	type PublisherApiToggle,
	type ToggleAuditEntry,
	describeAction,
	describeScope,
	disableToggle,
	enableToggle,
	fetchAudit,
	fetchScopes,
	fetchToggle,
	fetchToggleList,
	formatRelative,
	sortTogglesByRecency,
	toggleStatusLabel,
	upsertToggle,
	upsertToggleSchema,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — mirror PublisherApiPage so the visual identity stays
// consistent across dashboards without pulling a shared CSS module.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"inline-flex items-center justify-center rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"inline-flex items-center justify-center rounded-[6px] border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const BADGE_ON =
	"inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700";
const BADGE_OFF =
	"inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-600";

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export function PublisherApiTogglePage() {
	const [rows, setRows] = useState<PublisherApiToggle[]>([]);
	const [loadingRows, setLoadingRows] = useState(false);
	const [rowsError, setRowsError] = useState<string | null>(null);
	const [availableScopes, setAvailableScopes] = useState<ApiScope[]>([
		...AVAILABLE_SCOPES,
	]);

	const [selectedAffiliate, setSelectedAffiliate] = useState<string>("");
	const [audit, setAudit] = useState<ToggleAuditEntry[]>([]);
	const [loadingAudit, setLoadingAudit] = useState(false);

	const reloadList = useCallback(async () => {
		setLoadingRows(true);
		setRowsError(null);
		try {
			const fresh = await fetchToggleList();
			setRows(sortTogglesByRecency(fresh));
		} catch (err) {
			setRowsError(
				err instanceof Error ? err.message : "Unable to load toggles.",
			);
		} finally {
			setLoadingRows(false);
		}
	}, []);

	const reloadAudit = useCallback(async (id: string) => {
		const trimmed = id.trim();
		if (!trimmed) {
			setAudit([]);
			return;
		}
		setLoadingAudit(true);
		try {
			const entries = await fetchAudit(trimmed);
			setAudit(entries);
		} catch {
			setAudit([]);
		} finally {
			setLoadingAudit(false);
		}
	}, []);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			const [scopesRes] = await Promise.allSettled([fetchScopes()]);
			if (cancelled) return;
			if (scopesRes.status === "fulfilled" && scopesRes.value.length > 0) {
				setAvailableScopes(scopesRes.value);
			}
			await reloadList();
		})();
		return () => {
			cancelled = true;
		};
	}, [reloadList]);

	useEffect(() => {
		if (selectedAffiliate) {
			void reloadAudit(selectedAffiliate);
		} else {
			setAudit([]);
		}
	}, [selectedAffiliate, reloadAudit]);

	return (
		<main className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-14">
			<Breadcrumb />
			<Heading />
			<UpsertCard
				availableScopes={availableScopes}
				defaultAffiliateId={selectedAffiliate}
				onSaved={async (affiliateId) => {
					setSelectedAffiliate(affiliateId);
					await reloadList();
					await reloadAudit(affiliateId);
				}}
			/>
			<TogglesCard
				rows={rows}
				loading={loadingRows}
				error={rowsError}
				selectedAffiliate={selectedAffiliate}
				onSelect={setSelectedAffiliate}
				onToggle={async (affiliateId, nextEnabled) => {
					try {
						const action = nextEnabled
							? enableToggle(affiliateId)
							: disableToggle(affiliateId);
						await action;
						await reloadList();
						if (affiliateId === selectedAffiliate) {
							await reloadAudit(affiliateId);
						}
					} catch (err) {
						setRowsError(
							err instanceof Error ? err.message : "Unable to flip toggle.",
						);
					}
				}}
			/>
			<AuditCard
				selectedAffiliate={selectedAffiliate}
				entries={audit}
				loading={loadingAudit}
			/>
			<DocHint />
		</main>
	);
}

export default PublisherApiTogglePage;

// ---------------------------------------------------------------------------
// Header fragments
// ---------------------------------------------------------------------------

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5 text-zinc-600">
						Publishers
					</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">API access</li>
			</ol>
		</nav>
	);
}

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Publishers
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Publisher API access
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Provision or revoke partner API access per affiliate. Each toggle
				controls which surfaces the publisher can reach (campaigns, stats,
				creatives, payouts, conversions) plus an optional per-minute rate
				ceiling. Every flip is captured in the audit trail below.
			</p>
		</header>
	);
}

// ---------------------------------------------------------------------------
// Upsert card
// ---------------------------------------------------------------------------

interface UpsertState {
	affiliateId: string;
	enabled: boolean;
	scopes: ApiScope[];
	rateLimit: string;
	note: string;
	actor: string;
	submitting: boolean;
	errors: Record<string, string>;
	serverError: string | null;
	savedAt: number | null;
}

function UpsertCard(props: {
	availableScopes: ApiScope[];
	defaultAffiliateId: string;
	onSaved: (affiliateId: string) => Promise<void>;
}) {
	const [state, setState] = useState<UpsertState>({
		affiliateId: props.defaultAffiliateId,
		enabled: true,
		scopes: [],
		rateLimit: "",
		note: "",
		actor: "",
		submitting: false,
		errors: {},
		serverError: null,
		savedAt: null,
	});

	useEffect(() => {
		if (props.defaultAffiliateId && props.defaultAffiliateId !== state.affiliateId) {
			setState((prev) => ({ ...prev, affiliateId: props.defaultAffiliateId }));
			void hydrateFromServer(props.defaultAffiliateId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.defaultAffiliateId]);

	async function hydrateFromServer(id: string) {
		try {
			const current = await fetchToggle(id);
			if (!current) return;
			setState((prev) => ({
				...prev,
				enabled: current.toggle.enabled,
				scopes: [...current.toggle.scopes],
				rateLimit:
					current.toggle.rate_limit_per_min === null
						? ""
						: String(current.toggle.rate_limit_per_min),
				note: current.toggle.note ?? "",
				actor: current.toggle.updated_by ?? prev.actor,
				errors: {},
				serverError: null,
			}));
		} catch {
			/* ignore — user can still edit from scratch */
		}
	}

	function toggleScope(scope: ApiScope) {
		setState((prev) => {
			const next = prev.scopes.includes(scope)
				? prev.scopes.filter((s) => s !== scope)
				: [...prev.scopes, scope];
			return {
				...prev,
				scopes: next,
				errors: { ...prev.errors, scopes: "" },
			};
		});
	}

	async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const parsed = upsertToggleSchema.safeParse({
			affiliateId: state.affiliateId,
			enabled: state.enabled,
			scopes: state.scopes,
			rateLimitPerMin: state.rateLimit,
			note: state.note,
			updatedBy: state.actor,
		});
		if (!parsed.success) {
			setState((prev) => ({
				...prev,
				errors: parsed.errors,
				serverError: null,
			}));
			return;
		}
		setState((prev) => ({
			...prev,
			submitting: true,
			errors: {},
			serverError: null,
		}));
		try {
			await upsertToggle(parsed.data);
			await props.onSaved(parsed.data.affiliate_id);
			setState((prev) => ({
				...prev,
				submitting: false,
				savedAt: Date.now(),
			}));
		} catch (err) {
			setState((prev) => ({
				...prev,
				submitting: false,
				serverError:
					err instanceof Error ? err.message : "Unable to save toggle.",
			}));
		}
	}

	return (
		<section
			aria-label="Provision API access"
			className={`${CARD} flex flex-col gap-4 p-6`}
			data-testid="upsert-card"
		>
			<header className="flex flex-col gap-1">
				<h2 className="text-lg font-semibold text-zinc-900">
					Provision access
				</h2>
				<p className={HINT}>
					Create or replace the API access state for one affiliate. Upserts
					are audited automatically.
				</p>
			</header>
			<form onSubmit={onSubmit} className="flex flex-col gap-4">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Affiliate ID</span>
						<input
							type="text"
							className={INPUT}
							value={state.affiliateId}
							onChange={(e) =>
								setState((prev) => ({
									...prev,
									affiliateId: e.target.value,
									errors: { ...prev.errors, affiliateId: "" },
								}))
							}
							placeholder="aff_example"
							autoComplete="off"
							data-testid="upsert-affiliate"
						/>
						{state.errors.affiliateId ? (
							<span className={ERROR_TEXT}>{state.errors.affiliateId}</span>
						) : (
							<span className={HINT}>
								Letters, digits, '-' and '_' (max 64 chars).
							</span>
						)}
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Rate limit (req / min)</span>
						<input
							type="number"
							inputMode="numeric"
							min={1}
							step={1}
							className={INPUT}
							value={state.rateLimit}
							onChange={(e) =>
								setState((prev) => ({
									...prev,
									rateLimit: e.target.value,
									errors: { ...prev.errors, rateLimitPerMin: "" },
								}))
							}
							placeholder="optional (uses default)"
						/>
						{state.errors.rateLimitPerMin ? (
							<span className={ERROR_TEXT}>
								{state.errors.rateLimitPerMin}
							</span>
						) : (
							<span className={HINT}>
								Leave blank for the platform default.
							</span>
						)}
					</label>
				</div>

				<fieldset className="flex flex-col gap-2">
					<legend className={LABEL}>Scopes</legend>
					<div className="flex flex-wrap gap-2">
						{props.availableScopes.map((scope) => {
							const on = state.scopes.includes(scope);
							return (
								<button
									key={scope}
									type="button"
									onClick={() => toggleScope(scope)}
									className={
										on
											? "inline-flex items-center rounded-[6px] border border-emerald-500 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-800"
											: "inline-flex items-center rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
									}
									data-testid={`upsert-scope-${scope}`}
									aria-pressed={on}
								>
									{describeScope(scope)}
								</button>
							);
						})}
					</div>
					{state.errors.scopes ? (
						<span className={ERROR_TEXT}>{state.errors.scopes}</span>
					) : (
						<span className={HINT}>
							Pick at least one scope to enable access.
						</span>
					)}
				</fieldset>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Admin note</span>
						<textarea
							className={`${INPUT} min-h-[72px]`}
							value={state.note}
							onChange={(e) =>
								setState((prev) => ({
									...prev,
									note: e.target.value,
									errors: { ...prev.errors, note: "" },
								}))
							}
							placeholder="Why is this access being granted?"
							maxLength={500}
						/>
						{state.errors.note ? (
							<span className={ERROR_TEXT}>{state.errors.note}</span>
						) : (
							<span className={HINT}>Stored on the toggle row.</span>
						)}
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Acting admin</span>
						<input
							type="text"
							className={INPUT}
							value={state.actor}
							onChange={(e) =>
								setState((prev) => ({
									...prev,
									actor: e.target.value,
									errors: { ...prev.errors, updatedBy: "" },
								}))
							}
							placeholder="admin.alice"
							autoComplete="off"
						/>
						{state.errors.updatedBy ? (
							<span className={ERROR_TEXT}>{state.errors.updatedBy}</span>
						) : (
							<span className={HINT}>
								Optional — falls back to the x-actor-id header.
							</span>
						)}
					</label>
				</div>

				<label className="inline-flex items-center gap-2">
					<input
						type="checkbox"
						className="h-4 w-4 rounded-[4px] border-zinc-300 text-emerald-600 focus:ring-emerald-500"
						checked={state.enabled}
						onChange={(e) =>
							setState((prev) => ({ ...prev, enabled: e.target.checked }))
						}
						data-testid="upsert-enabled"
					/>
					<span className={LABEL}>API access enabled</span>
				</label>

				{state.serverError ? (
					<p className={ERROR_TEXT}>{state.serverError}</p>
				) : null}
				{state.savedAt ? (
					<p className="text-xs text-emerald-700">
						Saved {formatRelative(Math.floor(state.savedAt / 1000))}.
					</p>
				) : null}

				<div className="flex flex-wrap gap-2">
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={state.submitting}
						data-testid="upsert-submit"
					>
						{state.submitting ? "Saving..." : "Save toggle"}
					</button>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={() =>
							setState((prev) => ({
								...prev,
								affiliateId: "",
								enabled: true,
								scopes: [],
								rateLimit: "",
								note: "",
								actor: "",
								errors: {},
								serverError: null,
								savedAt: null,
							}))
						}
					>
						Reset
					</button>
				</div>
			</form>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Toggles list card
// ---------------------------------------------------------------------------

function TogglesCard(props: {
	rows: PublisherApiToggle[];
	loading: boolean;
	error: string | null;
	selectedAffiliate: string;
	onSelect: (id: string) => void;
	onToggle: (id: string, nextEnabled: boolean) => Promise<void>;
}) {
	const { rows, loading, error, selectedAffiliate, onSelect, onToggle } =
		props;

	const summary = useMemo(() => {
		let on = 0;
		let off = 0;
		for (const r of rows) {
			if (r.enabled) on += 1;
			else off += 1;
		}
		return { on, off, total: rows.length };
	}, [rows]);

	return (
		<section
			aria-label="Provisioned affiliates"
			className={`${CARD} flex flex-col gap-4 p-6`}
			data-testid="toggles-card"
		>
			<header className="flex flex-col gap-1">
				<h2 className="text-lg font-semibold text-zinc-900">
					Provisioned affiliates
				</h2>
				<p className={HINT}>
					{summary.total === 0
						? "No affiliates have been provisioned yet."
						: `${summary.on} enabled, ${summary.off} disabled, ${summary.total} total.`}
				</p>
			</header>
			{error ? (
				<p className={ERROR_TEXT} role="alert">
					{error}
				</p>
			) : null}
			<div className="overflow-x-auto">
				<table className="w-full border-collapse text-sm">
					<thead>
						<tr className="border-b border-zinc-200 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
							<th className="px-3 py-2">Affiliate</th>
							<th className="px-3 py-2">Status</th>
							<th className="px-3 py-2">Scopes</th>
							<th className="px-3 py-2">Rate limit</th>
							<th className="px-3 py-2">Updated</th>
							<th className="px-3 py-2 text-right">Actions</th>
						</tr>
					</thead>
					<tbody>
						{loading && rows.length === 0 ? (
							<tr>
								<td className="px-3 py-4 text-zinc-500" colSpan={6}>
									Loading toggles...
								</td>
							</tr>
						) : null}
						{rows.length === 0 && !loading ? (
							<tr>
								<td className="px-3 py-4 text-zinc-500" colSpan={6}>
									Use the form above to provision your first affiliate.
								</td>
							</tr>
						) : null}
						{rows.map((row) => {
							const selected = row.affiliate_id === selectedAffiliate;
							return (
								<tr
									key={row.id}
									className={
										selected
											? "border-b border-zinc-100 bg-emerald-50/50"
											: "border-b border-zinc-100 hover:bg-zinc-50"
									}
									data-testid={`toggle-row-${row.affiliate_id}`}
								>
									<td className="px-3 py-2 font-medium text-zinc-900">
										<button
											type="button"
											className="rounded-[6px] px-1 py-0.5 text-left hover:bg-white"
											onClick={() => onSelect(row.affiliate_id)}
										>
											{row.affiliate_id}
										</button>
									</td>
									<td className="px-3 py-2">
										<span className={row.enabled ? BADGE_ON : BADGE_OFF}>
											{toggleStatusLabel(row)}
										</span>
									</td>
									<td className="px-3 py-2 text-zinc-700">
										{row.scopes.length === 0
											? "—"
											: row.scopes.map(describeScope).join(", ")}
									</td>
									<td className="px-3 py-2 text-zinc-700">
										{row.rate_limit_per_min ?? "default"}
									</td>
									<td className="px-3 py-2 text-zinc-500">
										{formatRelative(row.updated_at)}
									</td>
									<td className="px-3 py-2 text-right">
										{row.enabled ? (
											<button
												type="button"
												className={BTN_DANGER}
												onClick={() => onToggle(row.affiliate_id, false)}
												data-testid={`row-disable-${row.affiliate_id}`}
											>
												Disable
											</button>
										) : (
											<button
												type="button"
												className={BTN_SECONDARY}
												onClick={() => onToggle(row.affiliate_id, true)}
												data-testid={`row-enable-${row.affiliate_id}`}
											>
												Enable
											</button>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Audit card
// ---------------------------------------------------------------------------

function AuditCard(props: {
	selectedAffiliate: string;
	entries: ToggleAuditEntry[];
	loading: boolean;
}) {
	const { selectedAffiliate, entries, loading } = props;

	return (
		<section
			aria-label="Audit trail"
			className={`${CARD} flex flex-col gap-4 p-6`}
			data-testid="audit-card"
		>
			<header className="flex flex-col gap-1">
				<h2 className="text-lg font-semibold text-zinc-900">Audit trail</h2>
				<p className={HINT}>
					{selectedAffiliate
						? `History for ${selectedAffiliate}, newest first.`
						: "Select an affiliate above to see their toggle history."}
				</p>
			</header>
			{loading ? (
				<p className={HINT}>Loading audit entries...</p>
			) : null}
			{!loading && selectedAffiliate && entries.length === 0 ? (
				<p className={HINT}>
					No audit entries yet. Saving the form above records one.
				</p>
			) : null}
			{entries.length > 0 ? (
				<ol className="flex flex-col gap-2">
					{entries.map((entry) => (
						<li
							key={entry.id}
							className="rounded-[6px] border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-700"
							data-testid={`audit-entry-${entry.id}`}
						>
							<div className="flex flex-wrap items-center gap-2">
								<span className="font-semibold text-zinc-900">
									{describeAction(entry.action)}
								</span>
								<span className={entry.next_enabled ? BADGE_ON : BADGE_OFF}>
									{entry.next_enabled ? "enabled" : "disabled"}
								</span>
								<span className="text-zinc-500">
									{formatRelative(entry.created_at)}
								</span>
								{entry.actor ? (
									<span className="text-zinc-500">by {entry.actor}</span>
								) : null}
							</div>
							{entry.next_scopes && entry.next_scopes.length > 0 ? (
								<p className="mt-1 text-zinc-600">
									Scopes: {entry.next_scopes.map(describeScope).join(", ")}
								</p>
							) : null}
							{entry.note ? (
								<p className="mt-1 italic text-zinc-600">&ldquo;{entry.note}&rdquo;</p>
							) : null}
						</li>
					))}
				</ol>
			) : null}
		</section>
	);
}

// ---------------------------------------------------------------------------
// Doc hint
// ---------------------------------------------------------------------------

function DocHint() {
	return (
		<aside className="rounded-[6px] border border-sky-200 bg-sky-50 px-4 py-3 text-xs text-sky-800">
			<p className="font-medium">Headers for partner probes</p>
			<p className="mt-1 text-sky-700">
				The worker reads <code className="rounded bg-white/70 px-1 py-0.5">x-tenant-id</code> and
				<code className="rounded bg-white/70 px-1 py-0.5"> x-actor-id</code> to attribute flips.
				Toggle flips are idempotent — calling enable twice is safe and is recorded as
				an &ldquo;update&rdquo; entry.
			</p>
		</aside>
	);
}
